import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { accessService } from '../services/access.service';
import { Photo, AccessInfo } from '../types';
import PhotoViewer from '../components/parent/PhotoViewer';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import { Camera, Download, User, Shield, Star, Smartphone, Monitor, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { useDeviceDetection } from '../utils/deviceDetection';
import { 
  downloadPhotosOptimized, 
  estimateDownloadTime, 
  getDownloadStrategyDescription,
  DownloadProgress 
} from '../utils/downloadHelpers';

export default function ParentAccess() {
  const { uuid } = useParams<{ uuid: string }>();
  const [selectedPhotos, setSelectedPhotos] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showUltraHDModal, setShowUltraHDModal] = useState(false);
  const [ultraHDEmail, setUltraHDEmail] = useState('');
  const [downloadProgress, setDownloadProgress] = useState<DownloadProgress | null>(null);
  const [showDownloadInfo, setShowDownloadInfo] = useState(false);
  
  const deviceInfo = useDeviceDetection();

  // Verify access
  const { 
    data: accessInfo, 
    isLoading: verifyLoading, 
    error: verifyError 
  } = useQuery<AccessInfo, Error>(
    ['access-verify', uuid],
    () => accessService.verifyAccess(uuid!),
    {
      enabled: !!uuid,
      retry: false,
    }
  );

  // Get photos
  const { 
    data: photosData, 
    isLoading: photosLoading 
  } = useQuery(
    ['access-photos', uuid, currentPage],
    () => accessService.getAccessiblePhotos(uuid!, currentPage, 50),
    {
      enabled: !!uuid && !!accessInfo,
      keepPreviousData: true,
    }
  );

  // Enhanced download mutation with device-specific optimization
  const downloadMutation = useMutation(
    async (photoIds: number[]) => {
      if (!photosData?.data || !accessInfo) {
        throw new Error('Données manquantes pour le téléchargement');
      }
      
      const selectedPhotoObjects = photosData.data.filter(photo => 
        photoIds.includes(photo.id)
      );
      
      const tagName = accessInfo.tag.name;
      
      return downloadPhotosOptimized(
        uuid!,
        selectedPhotoObjects,
        tagName,
        setDownloadProgress
      );
    },
    {
      onMutate: () => {
        setDownloadProgress({ completed: 0, total: selectedPhotos.length, percentage: 0 });
      },
      onSuccess: (result) => {
        setDownloadProgress(null);
        
        if (result.failed.length === 0) {
          toast.success(
            deviceInfo.isMobile || deviceInfo.isTablet 
              ? `${result.successful} photo(s) téléchargée(s) individuellement`
              : `${result.successful} photo(s) téléchargée(s) en archive`
          );
        } else {
          toast.success(`${result.successful} photo(s) téléchargée(s)`);
          if (result.failed.length > 0) {
            toast.error(`${result.failed.length} photo(s) ont échoué`);
          }
        }
        
        setSelectedPhotos([]);
      },
      onError: (error: any) => {
        setDownloadProgress(null);
        console.error('Download error:', error);
        toast.error(error.message || 'Erreur lors du téléchargement');
      },
    }
  );

  // UltraHD request mutation
  const ultraHDRequestMutation = useMutation(
    (data: { photoIds: number[]; email: string }) => 
      accessService.requestUltraHDPhotos(uuid!, data.photoIds, data.email),
    {
      onSuccess: () => {
        toast.success('Demande envoyée à l\'administrateur');
        setShowUltraHDModal(false);
        setUltraHDEmail('');
        setSelectedPhotos([]);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || 'Erreur lors de l\'envoi de la demande');
      },
    }
  );

  if (!uuid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600">Lien invalide</h1>
          <p className="mt-2 text-gray-600">Le lien d'accès n'est pas valide.</p>
        </div>
      </div>
    );
  }

  if (verifyLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="large" text="Vérification de l'accès..." />
      </div>
    );
  }

  if (verifyError || !accessInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Shield className="mx-auto h-12 w-12 text-red-500" />
          <h1 className="mt-4 text-2xl font-bold text-gray-900">Accès refusé</h1>
          <p className="mt-2 text-gray-600">
            Ce lien d'accès n'est pas valide ou a expiré.
          </p>
        </div>
      </div>
    );
  }

  const photos = photosData?.data || [];
  const pagination = photosData?.pagination;

  const handlePhotoSelect = (photo: Photo) => {
    setSelectedPhotos(prev => {
      if (prev.includes(photo.id)) {
        return prev.filter(id => id !== photo.id);
      } else {
        return [...prev, photo.id];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedPhotos.length === photos.length) {
      setSelectedPhotos([]);
    } else {
      setSelectedPhotos(photos.map(photo => photo.id));
    }
  };

  const handleDownload = () => {
    if (selectedPhotos.length === 0) {
      toast.error('Veuillez sélectionner au moins une photo');
      return;
    }
    
    downloadMutation.mutate(selectedPhotos);
  };

  const handleRequestUltraHD = () => {
    if (selectedPhotos.length === 0) {
      toast.error('Veuillez sélectionner au moins une photo');
      return;
    }
    setShowUltraHDModal(true);
  };

  const handleSubmitUltraHDRequest = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!ultraHDEmail.trim()) {
      toast.error('Veuillez saisir votre email');
      return;
    }
    
    if (selectedPhotos.length === 0) {
      toast.error('Aucune photo sélectionnée');
      return;
    }
    
    ultraHDRequestMutation.mutate({
      photoIds: selectedPhotos,
      email: ultraHDEmail.trim()
    });
  };

  const handleCloseUltraHDModal = () => {
    setShowUltraHDModal(false);
    setUltraHDEmail('');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <Camera className="h-8 w-8 text-primary-600" />
              <div className="ml-4">
                <h1 className="text-xl font-bold text-gray-900">
                  Photos de {accessInfo.tag.name}
                </h1>
                <p className="text-sm text-gray-500">Accès sécurisé</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4">
              {/* Device indicator */}
              <div className="hidden sm:flex items-center text-xs text-gray-500">
                {deviceInfo.isMobile ? (
                  <Smartphone className="h-4 w-4 mr-1" />
                ) : (
                  <Monitor className="h-4 w-4 mr-1" />
                )}
                <span className="hidden md:inline">
                  {deviceInfo.isMobile ? 'Mobile' : deviceInfo.isTablet ? 'Tablette' : 'Ordinateur'}
                </span>
              </div>

              {selectedPhotos.length > 0 && (
                <>
                  {/* Download info button */}
                  <button
                    onClick={() => setShowDownloadInfo(!showDownloadInfo)}
                    className="btn-outline p-2 sm:px-3 sm:py-2"
                    title="Informations de téléchargement"
                  >
                    <Info className="h-4 w-4" />
                    <span className="hidden sm:ml-2 sm:inline">Info</span>
                  </button>

                  <button
                    onClick={handleDownload}
                    disabled={downloadMutation.isLoading || !!downloadProgress}
                    className="btn-primary disabled:opacity-50 flex-shrink-0"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {downloadMutation.isLoading || downloadProgress
                      ? (
                        <span className="flex items-center">
                          {downloadProgress ? (
                            <>
                              <span className="hidden sm:inline mr-2">
                                {downloadProgress.currentFile ? 
                                  `${downloadProgress.currentFile.substring(0, 15)}...` : 
                                  'Téléchargement'
                                }
                              </span>
                              <span>{downloadProgress.percentage}%</span>
                            </>
                          ) : (
                            'Préparation...'
                          )}
                        </span>
                      )
                      : `Télécharger (${selectedPhotos.length})`
                    }
                  </button>
                  
                  <button
                    onClick={handleRequestUltraHD}
                    disabled={ultraHDRequestMutation.isLoading}
                    className="btn-outline disabled:opacity-50 hidden sm:flex"
                  >
                    <Star className="h-4 w-4 mr-2" />
                    {ultraHDRequestMutation.isLoading 
                      ? 'Envoi...' 
                      : 'UltraHD'
                    }
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Download Info Panel */}
        {showDownloadInfo && selectedPhotos.length > 0 && (
          <div className="mb-6 card p-4 bg-blue-50 border-blue-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-900 mb-2">
                  Informations de téléchargement
                </h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <div className="flex items-center">
                    {deviceInfo.isMobile ? (
                      <Smartphone className="h-4 w-4 mr-2" />
                    ) : (
                      <Monitor className="h-4 w-4 mr-2" />
                    )}
                    <span>{getDownloadStrategyDescription()}</span>
                  </div>
                  
                  <div className="flex items-center">
                    <Download className="h-4 w-4 mr-2" />
                    <span>
                      {selectedPhotos.length} photo(s) sélectionnée(s) • 
                      Temps estimé: {estimateDownloadTime(selectedPhotos.length)}
                    </span>
                  </div>
                  
                  {(deviceInfo.isMobile || deviceInfo.isTablet) && (
                    <div className="text-xs text-blue-600 mt-2 p-2 bg-blue-100 rounded">
                      📱 <strong>Mode mobile détecté:</strong> Les photos seront téléchargées 
                      individuellement pour une meilleure compatibilité avec votre appareil.
                    </div>
                  )}
                  
                  {deviceInfo.isDesktop && (
                    <div className="text-xs text-blue-600 mt-2 p-2 bg-blue-100 rounded">
                      💻 <strong>Mode ordinateur:</strong> Les photos seront téléchargées 
                      dans une archive ZIP pour plus d'efficacité.
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowDownloadInfo(false)}
                className="text-blue-400 hover:text-blue-600 ml-4"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Download Progress */}
        {downloadProgress && (
          <div className="mb-6 card p-4 bg-green-50 border-green-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-green-900">
                Téléchargement en cours...
              </h3>
              <span className="text-sm text-green-700">
                {downloadProgress.completed}/{downloadProgress.total}
              </span>
            </div>
            
            <div className="w-full bg-green-200 rounded-full h-2 mb-2">
              <div 
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${downloadProgress.percentage}%` }}
              ></div>
            </div>
            
            {downloadProgress.currentFile && (
              <p className="text-xs text-green-600">
                📥 {downloadProgress.currentFile}
              </p>
            )}
          </div>
        )}

        {/* Stats */}
        <div className="mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="card p-4">
            <div className="flex items-center">
              <Camera className="h-8 w-8 text-primary-600" />
              <div className="ml-4">
                <p className="text-2xl font-semibold text-gray-900">
                  {pagination?.total || 0}
                </p>
                <p className="text-sm text-gray-600">Photos disponibles</p>
              </div>
            </div>
          </div>
          
          <div className="card p-4">
            <div className="flex items-center">
              <User className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-2xl font-semibold text-gray-900">
                  {photos.filter(p => !p.isGroupPhoto).length}
                </p>
                <p className="text-sm text-gray-600">Photos individuelles</p>
              </div>
            </div>
          </div>
          
          <div className="card p-4">
            <div className="flex items-center">
              <User className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-2xl font-semibold text-gray-900">
                  {photos.filter(p => p.isGroupPhoto).length}
                </p>
                <p className="text-sm text-gray-600">Photos de groupe</p>
              </div>
            </div>
          </div>
        </div>

        {/* Photo Viewer */}
        {photosLoading ? (
          <LoadingSpinner size="large" text="Chargement des photos..." />
        ) : (
          <PhotoViewer
            photos={photos}
            selectedPhotos={selectedPhotos}
            onPhotoSelect={handlePhotoSelect}
            onSelectAll={handleSelectAll}
            onDownload={handleDownload}
            pagination={pagination}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
            isDownloading={downloadMutation.isLoading}
          />
        )}
      </div>

      {/* UltraHD Request Modal */}
      {showUltraHDModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Demander les photos en UltraHD
              </h3>
              <button 
                onClick={handleCloseUltraHDModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">
                Demandez à recevoir {selectedPhotos.length} photo(s) en qualité originale (UltraHD) pour impression.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Votre demande sera envoyée à l'administrateur qui vous contactera pour vous fournir les fichiers haute définition.
              </p>
            </div>

            <form onSubmit={handleSubmitUltraHDRequest}>
              <div className="mb-4">
                <label htmlFor="ultraHDEmail" className="block text-sm font-medium text-gray-700">
                  Votre email *
                </label>
                <input
                  type="email"
                  id="ultraHDEmail"
                  value={ultraHDEmail}
                  onChange={(e) => setUltraHDEmail(e.target.value)}
                  className="mt-1 input"
                  placeholder="votre.email@example.com"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  L'administrateur utilisera cet email pour vous contacter
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  type="submit"
                  disabled={ultraHDRequestMutation.isLoading}
                  className="flex-1 btn-primary disabled:opacity-50"
                >
                  <Star className="h-4 w-4 mr-2" />
                  {ultraHDRequestMutation.isLoading ? 'Envoi...' : 'Envoyer la demande'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseUltraHDModal}
                  className="btn-outline"
                >
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}